import React, { useEffect } from 'react';
import stylesTexto from './CampoTexto.module.css';

type CampoMascaraProps = {
  label?: string;
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  props?: any;
  disabled?: boolean;
  error?: string;
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  noBorder?: boolean;
  masks?: string[];
  maskChar?: string;
};

const CampoMascara = ({
  label,
  value,
  placeholder,
  onChange,
  disabled,
  error,
  prepend,
  append,
  noBorder,
  masks,
  maskChar = '_',
  ...props
}: CampoMascaraProps) => {
  const maskedToRaw = (masked: string) => {
    if (!masked) return masked;

    const result = masked.replace(/[^0-9a-z]+/gi, '');
    return result;
  };

  const sortedMasks = masks?.sort((a, b) => a.length - b.length) ?? [];
  const rawMasks = sortedMasks.map(mask => maskedToRaw(mask));
  const rawToMasked = (raw: string) => {
    const selectedMaskIdx = rawMasks.findIndex(
      mask => raw.length <= mask.length,
    );
    const selectedMask = sortedMasks?.[selectedMaskIdx];

    if (!selectedMask) return raw;

    let result = '';
    let rawIndex = 0;

    for (let i = 0; i < selectedMask.length; i++) {
      if (selectedMask[i] === '9') {
        if (rawIndex < raw.length && /\d/.test(raw[rawIndex])) {
          result += raw[rawIndex];
          rawIndex++;
        } else {
          break;
        }
      } else if (selectedMask[i] === 'A') {
        if (rawIndex < raw.length && /[a-zA-Z]/.test(raw[rawIndex])) {
          result += raw[rawIndex];
          rawIndex++;
        } else {
          break;
        }
      } else {
        result += selectedMask[i];
      }
    }

    return result;
  };
  const [maskedValue, setMaskedValue] = React.useState(value ? rawToMasked(value) : (value));
  const [cursorPosition, setCursorPosition] = React.useState<{
    start: number;
    end: number | null;
    dir: 'forward' | 'backward' | 'none' | undefined;
  }>({
    start: 0,
    end: 0,
    dir: 'forward',
  });
  const [inserted, setInserted] = React.useState(false);

  const onMaskedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = maskedToRaw(e.target.value);

    const masked = rawToMasked(raw);
    if (masked === null) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    setMaskedValue(masked);

    if (e.target.selectionStart != null) {
      if (!inserted) {
        setCursorPosition({
          start: e.target.selectionStart,
          end: e.target.selectionEnd,
          dir: e.target.selectionDirection ?? undefined,
        });
      } else {
        const diff = masked.length - e.target.value.length;
        setCursorPosition({
          start: e.target.selectionStart + diff,
          end: e.target.selectionEnd ? e.target.selectionEnd + diff : null,
          dir: e.target.selectionDirection ?? undefined,
        });
      }
    }
    onChange && onChange(raw);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setInserted(e.key !== 'Backspace');
  };

  useEffect(() => {
    if (!Boolean(value)) {
      setMaskedValue(value);
    } else {
      const newMasked = rawToMasked(value!);
      setMaskedValue(newMasked);
    }
  }, [value]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current!.setSelectionRange(
      cursorPosition.start,
      cursorPosition.end,
      cursorPosition.dir,
    );
  }, [cursorPosition]);

  return (
    <div>
      <label>{label}</label>
      <div
        className={[
          stylesTexto.container,
          error ? stylesTexto.error : null,
        ].join(' ')}
      >
        {prepend && <div className={stylesTexto.prepend}>{prepend}</div>}

        <input
          {...props}
          ref={inputRef}
          type="text"
          value={maskedValue}
          onChange={onMaskedChange}
          onKeyDown={onKeyDown}
          className={[stylesTexto.input].join(' ')}
          style={{ border: noBorder ? 'none' : undefined }}
          disabled={disabled}
          onSelect={(e: any) => {
            const raw = maskedToRaw(e.target.value);

            const masked = rawToMasked(raw);
            if (masked === null) {
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            const selectedMaskIdx = rawMasks.findIndex(
              mask => raw.length <= mask.length,
            );
            const selectedMask = sortedMasks?.[selectedMaskIdx];

            if (
              !raw.length &&
              selectedMask &&
              e.target.selectionStart === e.target.selectionEnd
            ) {
              const pos9 = selectedMask.indexOf('9');
              if (pos9 !== -1) {
                setCursorPosition({
                  start: pos9,
                  end: pos9,
                  dir: e.target.selectionDirection,
                });
              } else {
                const posA = selectedMask.indexOf('A');
                setCursorPosition({
                  start: posA,
                  end: posA,
                  dir: e.target.selectionDirection,
                });
              }
            }
          }}
        />

        {append && <div className={stylesTexto.append}>{append}</div>}
      </div>
      {error && <div className={stylesTexto.error}>{error}</div>}
    </div>
  );
};

export default CampoMascara;
